import { Schedule } from "@mui/icons-material";
import { Box, Chip, Tooltip } from "@mui/material";

type Props = {
  dueDate: string | Date | null | undefined;
  iconOnly?: boolean;
};

const formatDate = (dueDate: string | Date) => {
  const date = new Date(dueDate);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const isDueSoon = (dueDate: string | Date) => {
  const date = new Date(dueDate);
  if (Number.isNaN(date.getTime())) return false;

  const today = new Date();
  const diffDays = Math.ceil(
    (date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );
  return diffDays <= 3 && diffDays >= 0;
};

const isOverdue = (dueDate: string | Date) => {
  const date = new Date(dueDate);
  if (Number.isNaN(date.getTime())) return false;

  const today = new Date();
  return date < today;
};

export const TaskDueDateChip = ({ dueDate, iconOnly = false }: Props) => {
  if (!dueDate) return null;

  const formattedDate = formatDate(dueDate);
  if (!formattedDate) return null;

  const color = isOverdue(dueDate)
    ? "error"
    : isDueSoon(dueDate)
      ? "warning"
      : "default";

  if (iconOnly) {
    const circleColor =
      color === "error"
        ? "error.main"
        : color === "warning"
          ? "warning.main"
          : "text.secondary";

    return (
      <Tooltip title={formattedDate} placement="top">
        <Box
          component="span"
          data-testid="task-due-date-circle"
          sx={{
            alignItems: "center",
            border: 1,
            borderColor: circleColor,
            borderRadius: "50%",
            backgroundColor: "inherit",
            color: circleColor,
            display: "inline-flex",
            height: 24,
            justifyContent: "center",
            width: 24,
          }}
        >
          <Schedule fontSize="small" />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Chip
      icon={<Schedule fontSize="small" />}
      label={formattedDate}
      size="small"
      color={color}
      variant="outlined"
      sx={{ height: 24 }}
    />
  );
};
